import { createSlice, createDraftSafeSelector, PayloadAction } from '@reduxjs/toolkit';

interface IHistoryInfo {
  magazineId: string;
}

interface HistoryState {
  magazineList: IHistoryInfo[];
  magazineInfo: IHistoryInfo;
  actionResult: string;
  isLoading: boolean;
  error: string | null;
}

const InitialState: HistoryState = {
  magazineList: [],
  magazineInfo: {},
  actionResult: '',
  isLoading: false,
  error: '',
};

const reducers = {
  stateClear: (state: HistoryState, { payload }: PayloadAction<HistoryState>) => {
    state.magazineList = [];
    state.magazineInfo = {};
    state.actionResult = '';
    state.isLoading = false;
    state.error = '';
  },
  list: (state: HistoryState, { payload }: PayloadAction<HistoryState>) => {
    state.isLoading = true;
    state.actionResult = 'LIST_REQ';
    state.error = '';
  },
  listSuccess: (state: HistoryState, { payload }: PayloadAction<HistoryState>) => {
    state.magazineList = payload.magazineList;
    state.isLoading = false;
    state.actionResult = 'LIST_OK';
    state.error = '';
  },
  listFailure: (state: HistoryState, { payload }: PayloadAction<HistoryState>) => {
    state.isLoading = false;
    state.actionResult = 'LIST_ERR';
    state.error = payload.error;
  },
  detail: (state: HistoryState, { payload }: PayloadAction<HistoryState>) => {
    state.isLoading = true;
    state.actionResult = 'DETAIL_REQ';
    state.error = '';
  },
  detailSuccess: (state: HistoryState, { payload }: PayloadAction<HistoryState>) => {
    state.magazineInfo = payload.magazineInfo;
    state.isLoading = false;
    state.actionResult = 'DETAIL_OK';
    state.error = '';
  },
  detailFailure: (state: HistoryState, { payload }: PayloadAction<HistoryState>) => {
    state.isLoading = false;
    state.actionResult = 'DETAIL_ERR';
    state.error = payload.error;
  },
  add: (state: HistoryState, { payload }: PayloadAction<CsutomerHistoryState>) => {
    state.isLoading = true;
    state.actionResult = 'ADD_REQ';
    state.error = '';
  },
  addSuccess: (state: HistoryState, { payload }: PayloadAction<HistoryState>) => {
    state.isLoading = false;
    state.actionResult = 'ADD_OK';
    state.error = '';
  },
  addFailure: (state: HistoryState, action: PayloadAction<string>) => {
    state.isLoading = false;
    state.actionResult = 'ADD_ERR';
    state.error = action.payload;
  },
  edit: (state: HistoryState, { payload }: PayloadAction<CsutomerHistoryState>) => {
    state.isLoading = true;
    state.actionResult = 'EDIT_REQ';
    state.error = '';
  },
  editSuccess: (state: HistoryState, { payload }: PayloadAction<HistoryState>) => {
    state.isLoading = false;
    state.actionResult = 'EDIT_OK';
    state.error = '';
  },
  editFailure: (state: HistoryState, action: PayloadAction<string>) => {
    state.isLoading = false;
    state.actionResult = 'EDIT_ERR';
    state.error = action.payload;
  },
  reOrder: (state: HistoryState, { payload }: PayloadAction<CsutomerHistoryState>) => {
    state.isLoading = true;
    state.actionResult = 'REORDER_REQ';
    state.error = '';
  },
  reOrderSuccess: (state: HistoryState, { payload }: PayloadAction<HistoryState>) => {
    state.magazineList = payload.magazineList;
    state.isLoading = false;
    state.actionResult = 'REORDER_OK';
    state.error = '';
  },
  reOrderFailure: (state: HistoryState, action: PayloadAction<string>) => {
    state.isLoading = false;
    state.actionResult = 'REORDER_ERR';
    state.error = action.payload;
  },
  remove: (state: HistoryState, { payload }: PayloadAction<CsutomerHistoryState>) => {
    state.isLoading = true;
    state.actionResult = 'REMOVE_REQ';
    state.error = '';
  },
  removeSuccess: (state: HistoryState, { payload }: PayloadAction<HistoryState>) => {
    state.magazineList = payload.magazineList;
    state.isLoading = false;
    state.actionResult = 'REMOVE_OK';
    state.error = '';
  },
  removeFailure: (state: HistoryState, action: PayloadAction<string>) => {
    state.isLoading = false;
    state.actionResult = 'REMOVE_ERR';
    state.error = action.payload;
  },
  actionResultClear: (state: HistoryState) => {
    state.actionResult = '';
  },
};

const slice = createSlice({
  name: 'magazine',
  initialState: InitialState,
  reducers: reducers,
});

const selectMagazineList = createDraftSafeSelector(
  (state: HistoryState) => state.magazineList,
  (magazineList) => magazineList
);

const selectMagazineInfo = createDraftSafeSelector(
  (state: HistoryState) => state.magazineInfo,
  magazineInfo => magazineInfo,
);

const selectStatus = createDraftSafeSelector(
  (state: HistoryState) => state.actionResult,
  (state: HistoryState) => state.isLoading,
  (state: HistoryState) => state.error,
  (actionResult, isLoading, error) => ({
    actionResult,
    isLoading,
    error,
  }),
);

export const magazineSelector = {
  magazineList: state => selectMagazineList(state[MAGAZINE]),
  magazineInfo: state => selectMagazineInfo(state[MAGAZINE]),
  status: state => selectStatus(state[MAGAZINE]),
};

export const MAGAZINE = slice.name;
export const magazineReducer = slice.reducer;
export const magazineAction = slice.actions;
